/**
 * Шаблоны настроек рисунков.
 *
 * Шаблон — вид фигуры под своим именем: цвет, толщина, линия и то, что
 * у инструмента есть сверх этого, — уровни Фибоначчи, счёт позиции,
 * оформление текста. Один шаблон инструмента может быть «по умолчанию»:
 * новая фигура тогда сразу рисуется в нём, и красную горизонталь в два
 * пикселя больше не приходится настраивать заново каждый раз.
 *
 * Хранятся в профиле человека — одинаковы на компьютере и на телефоне.
 */

import { findKind, type DrawingKind } from './kinds.js';
import type { Drawing, LineStyle } from './types.js';

export interface TemplateStyle {
  readonly color: string;
  readonly width: number;
  readonly style: LineStyle;
  readonly fib?: Drawing['fib'];
  readonly position?: Drawing['position'];
  readonly textStyle?: Drawing['textStyle'];
  readonly options?: Drawing['options'];
}

export interface DrawingTemplate {
  readonly id: string;
  readonly kind: DrawingKind;
  readonly name: string;
  readonly look: TemplateStyle;
  /** Новые фигуры этого инструмента рисуются в нём. */
  readonly isDefault: boolean;
  readonly createdAt: number;
}

/** Вид фигуры — то, что попадает в шаблон. Точки и текст — нет: это содержание. */
export function lookOf(drawing: Drawing): TemplateStyle {
  return {
    color: drawing.color,
    width: drawing.width,
    style: drawing.style,
    ...(drawing.fib ? { fib: drawing.fib } : {}),
    ...(drawing.position ? { position: drawing.position } : {}),
    ...(drawing.textStyle ? { textStyle: drawing.textStyle } : {}),
    ...(drawing.options ? { options: drawing.options } : {}),
  };
}

export function applyLook(drawing: Drawing, look: TemplateStyle): Drawing {
  return { ...drawing, ...look };
}

export function defaultTemplate(templates: readonly DrawingTemplate[], kind: DrawingKind): DrawingTemplate | null {
  return templates.find((template) => template.kind === kind && template.isDefault) ?? null;
}

/** Шаблоны из профиля; непонятная запись пропускается. */
export function drawingTemplatesFrom(raw: unknown): DrawingTemplate[] {
  if (!Array.isArray(raw)) return [];
  const out: DrawingTemplate[] = [];
  for (const entry of raw) {
    if (typeof entry !== 'object' || entry === null) continue;
    const v = entry as Record<string, unknown>;
    if (typeof v['id'] !== 'string' || typeof v['kind'] !== 'string' || !findKind(v['kind'])) continue;
    if (typeof v['name'] !== 'string' || v['name'].trim() === '') continue;
    const look = v['look'] as Record<string, unknown> | undefined;
    if (!look || typeof look['color'] !== 'string' || typeof look['width'] !== 'number') continue;
    const style = look['style'] === 'dashed' || look['style'] === 'dotted' ? look['style'] : 'solid';
    out.push({
      id: v['id'],
      kind: v['kind'] as DrawingKind,
      name: v['name'].trim().slice(0, 40),
      look: {
        color: look['color'],
        width: look['width'],
        style,
        ...(typeof look['fib'] === 'object' && look['fib'] !== null ? { fib: look['fib'] as Drawing['fib'] } : {}),
        ...(typeof look['position'] === 'object' && look['position'] !== null ? { position: look['position'] as Drawing['position'] } : {}),
        ...(typeof look['textStyle'] === 'object' && look['textStyle'] !== null ? { textStyle: look['textStyle'] as Drawing['textStyle'] } : {}),
      },
      isDefault: v['isDefault'] === true,
      createdAt: typeof v['createdAt'] === 'number' ? v['createdAt'] : 0,
    });
  }
  return out;
}
